<template>
  <div class="create-home">
    <section v-for="group in groups" :key="group.label" class="method-group">
      <h2>{{ group.label }}</h2>
      <div class="method-grid">
        <router-link v-for="m in group.items" :key="m.to" :to="m.to" class="method-card">
          <span class="method-icon" :class="m.tone">
            <el-icon><component :is="m.icon" /></el-icon>
          </span>
          <div class="method-body">
            <strong>{{ m.title }}</strong>
            <p>{{ m.desc }}</p>
            <div class="method-tags">
              <span v-for="t in m.tags" :key="t">{{ t }}</span>
            </div>
          </div>
          <el-icon class="method-go"><ArrowRight /></el-icon>
        </router-link>
      </div>
    </section>

    <section class="agent-tip">
      <div>
        <strong>不确定用哪种？</strong>
        <span>直接告诉 Agent 目标，例如「给小红书矩阵写 5 篇写作技巧笔记，过审后发出去」。</span>
      </div>
      <el-button type="primary" plain @click="askAgent">问 Agent</el-button>
    </section>
  </div>
</template>

<script>
import { ArrowRight } from '@element-plus/icons-vue'
import { useAgentStore } from '../stores/agentStore'

const GROUPS = [
  {
    label: '图文',
    items: [
      {
        to: '/create/generate',
        title: '批量生成',
        desc: '按「主题 × 文章类型」矩阵一次生成多篇，自动审核去重，过审后可直接发布。',
        tags: ['小红书', '知乎', '头条'],
        icon: 'EditPen',
        tone: 'blue',
      },
      {
        to: '/create/zhihu-qa',
        title: '知乎问答',
        desc: '输入问题，联网检索后拆分回答方向，逐个方向写成回答。',
        tags: ['知乎', '联网检索'],
        icon: 'ChatDotSquare',
        tone: 'indigo',
      },
      {
        to: '/create/tabstory',
        title: 'Tabstory',
        desc: 'Quora GEO 发帖生成器：联网检索后生成适合 Quora 的问答帖子。',
        tags: ['Quora', '联网检索'],
        icon: 'Notebook',
        tone: 'violet',
      },
    ],
  },
  {
    label: '口播与视频',
    items: [
      {
        to: '/create/column-oral',
        title: '专栏口播',
        desc: '把专栏选题写成适合朗读的口播文案，可选联网检索。',
        tags: ['口播文案'],
        icon: 'Microphone',
        tone: 'orange',
      },
      {
        to: '/create/voice2video',
        title: '口播转视频',
        desc: '口播文案自动配素材、TTS 配音并合成视频。',
        tags: ['抖音', '视频'],
        icon: 'VideoCamera',
        tone: 'rose',
      },
    ],
  },
  {
    label: '改编',
    items: [
      {
        to: '/create/rewrite/bilibili',
        title: 'B站洗稿',
        desc: '视频链接或 UP 主主页 → 字幕 / 语音识别 → 改写成图文。',
        tags: ['B站', '批量'],
        icon: 'RefreshRight',
        tone: 'cyan',
      },
      {
        to: '/create/rewrite/douyin',
        title: '抖音洗稿',
        desc: '抖音视频 → 字幕 / 语音识别 → 改写成图文。',
        tags: ['抖音'],
        icon: 'RefreshRight',
        tone: 'cyan',
      },
      {
        to: '/create/rewrite/text',
        title: '文本洗稿',
        desc: '粘贴原文，并发生成多个改写版本并自动入库。',
        tags: ['多版本'],
        icon: 'DocumentCopy',
        tone: 'green',
      },
    ],
  },
]

export default {
  name: 'CreateHome',
  components: { ArrowRight },
  setup() {
    const agent = useAgentStore()
    const askAgent = () => agent.openPanel()
    return { groups: GROUPS, askAgent }
  },
}
</script>

<style scoped>
.create-home {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.method-group h2 {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--mp-text-muted);
}

.method-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.method-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  border-radius: var(--mp-radius-lg);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.method-card:hover {
  border-color: var(--mp-accent);
  box-shadow: var(--mp-shadow);
  transform: translateY(-1px);
}

.method-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-size: 18px;
  flex-shrink: 0;
}

.method-icon.blue { background: #edf3ff; color: #326cf4; }
.method-icon.indigo { background: #eef0ff; color: #4f5bd5; }
.method-icon.violet { background: #f4eeff; color: #7c4dda; }
.method-icon.orange { background: #fff3e8; color: #e07a1f; }
.method-icon.rose { background: #ffedf0; color: #d6455d; }
.method-icon.cyan { background: #e8f8fb; color: #138aa3; }
.method-icon.green { background: #e6f7f1; color: #16a476; }

.method-body {
  flex: 1;
  min-width: 0;
}

.method-body strong {
  display: block;
  font-size: 14px;
  color: var(--mp-text);
}

.method-body p {
  margin: 4px 0 8px;
  font-size: 12px;
  line-height: 1.55;
  color: var(--mp-text-secondary);
}

.method-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.method-tags span {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f5f8;
  color: var(--mp-text-secondary);
}

.method-go {
  align-self: center;
  color: var(--mp-text-muted);
}

.agent-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-radius: var(--mp-radius-lg);
  background: var(--mp-accent-soft);
  font-size: 12px;
  color: var(--mp-text-secondary);
}

.agent-tip strong {
  color: var(--mp-text);
  margin-right: 8px;
}

@media (max-width: 768px) {
  .method-grid {
    grid-template-columns: 1fr;
  }

  .agent-tip {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
